// Platform → Tenants → :id. Detail view: facilities, users, counts.
// Actions: edit fields, suspend/activate, soft-delete, hard-teardown,
// impersonate a tenant user.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { platformApi } from '../lib/api.js';

export default function PlatformTenantDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  async function load() {
    try { const r = await platformApi.tenant(id); setData(r); setErr(null); }
    catch (e) { setErr(e.body?.message || e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (err) return <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3">{err}</div>;
  if (!data) return <div className="text-slate-500">Loading…</div>;
  const { tenant, facilities, users, counts } = data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/platform" className="text-sm text-slate-500 hover:underline">← All tenants</Link>
          <h1 className="text-2xl font-bold">{tenant.name}</h1>
          <div className="text-sm text-slate-500 font-mono">{tenant.slug}</div>
        </div>
        <div className="flex gap-2">
          <SuspendToggle tenant={tenant} reload={load} />
          <SoftDeleteButton tenant={tenant} reload={load} />
        </div>
      </div>

      {tenant.deleted_at && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          Tenant soft-deleted on {new Date(tenant.deleted_at).toLocaleString()}. Use the
          hard-delete action below for permanent teardown (cascades through everything).
        </div>
      )}

      {/* Counts strip */}
      <div className="grid sm:grid-cols-4 gap-3">
        <Count label="Facilities" v={facilities?.length} />
        <Count label="Users" v={users?.length} />
        <Count label="Patients" v={counts.patients} />
        <Count label="Payments" v={counts.payments} />
      </div>

      {/* Edit settings */}
      <EditTenantForm tenant={tenant} onSaved={load} />

      {/* Facilities */}
      <Card title="Facilities">
        {facilities.length === 0 && <div className="text-sm text-slate-500 italic">None.</div>}
        <ul className="divide-y divide-slate-100 text-sm">
          {facilities.map(f => (
            <li key={f.id} className="py-2 flex items-center justify-between">
              <div>
                <div className="font-medium">{f.display_name || f.name}</div>
                <div className="text-xs text-slate-500 font-mono">{f.code}</div>
              </div>
              <span className="w-3 h-3 rounded" style={{ background: f.theme_primary || '#1d4ed8' }} />
            </li>
          ))}
        </ul>
      </Card>

      {/* Users + impersonate */}
      <Card title="Users">
        {users.length === 0 && <div className="text-sm text-slate-500 italic">No users yet.</div>}
        <ul className="divide-y divide-slate-100 text-sm">
          {users.map(u => (
            <li key={u.id} className="py-2 flex items-center justify-between">
              <div>
                <div className="font-medium flex items-center gap-2">
                  {u.full_name}
                  {u.must_change_password && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-normal">
                      must change password
                    </span>
                  )}
                  {!u.active && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700 font-normal">
                      inactive
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500">{u.email} · <span className="font-mono">{u.role}</span>
                  {u.last_login_at
                    ? <> · last login {new Date(u.last_login_at).toLocaleDateString()}</>
                    : <> · never logged in</>}
                </div>
              </div>
              <div className="flex gap-1">
                <ResetPasswordButton tenantId={id} user={u} />
                <ImpersonateButton tenantId={id} user={u} />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <SecretsSection tenantId={id} />

      {tenant.deleted_at && <HardDeleteSection tenant={tenant} />}
    </div>
  );
}

function SecretsSection({ tenantId }) {
  const [keys, setKeys]     = useState(null);
  const [err, setErr]       = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newVal, setNewVal] = useState('');
  const [busy, setBusy]     = useState(false);

  async function load() {
    try { const r = await platformApi.tenantSecrets(tenantId); setKeys(r.keys); setErr(null); }
    catch (e) { setErr(e.body?.message || e.message); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenantId]);

  async function add(e) {
    e.preventDefault();
    if (!newKey || !newVal) return;
    setBusy(true); setErr(null);
    try {
      await platformApi.tenantSecretSet(tenantId, { key: newKey.trim(), value: newVal });
      setNewKey(''); setNewVal(''); setShowAdd(false); load();
    } catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }
  async function reveal(key) {
    try {
      const r = await platformApi.tenantSecretGet(tenantId, key);
      window.prompt(`Value of ${key} (audited)`, r.value);
    } catch (e) { alert(e.body?.message || e.message); }
  }
  async function remove(key) {
    if (!confirm(`Delete secret ${key}?`)) return;
    try { await platformApi.tenantSecretDelete(tenantId, key); load(); }
    catch (e) { alert(e.body?.message || e.message); }
  }

  const COMMON_KEYS = [
    'stripe.secret_key', 'stripe.publishable_key', 'stripe.webhook_secret',
    'twilio.account_sid', 'twilio.auth_token', 'twilio.from',
    'sendgrid.api_key', 'sendgrid.from',
    'easypost.api_key', 'easypost.webhook_secret',
    'anthropic.api_key',
  ];

  return (
    <Card title="Tenant secrets">
      <p className="text-xs text-slate-500 mb-3">
        Vendor-account credentials encrypted at rest with AES-256-GCM. Reading a value
        is owner-only and audit-logged. Setting / deleting requires owner or support.
      </p>
      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2">{err}</div>}
      {keys === null && <div className="text-sm text-slate-500">Loading…</div>}
      {keys && keys.length === 0 && <div className="text-sm text-slate-500 italic mb-2">No secrets set yet.</div>}
      {keys && keys.length > 0 && (
        <ul className="divide-y divide-slate-100 text-sm mb-3">
          {keys.map(k => (
            <li key={k.key} className="py-2 flex items-center justify-between">
              <div>
                <div className="font-mono">{k.key}</div>
                <div className="text-xs text-slate-500">
                  rotated {new Date(k.last_rotated_at).toLocaleString()}
                </div>
              </div>
              <div className="flex gap-1">
                <button className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50"
                  onClick={() => reveal(k.key)}>Reveal</button>
                <button className="text-xs px-2 py-1 rounded border border-red-300 text-red-700 hover:bg-red-50"
                  onClick={() => remove(k.key)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
      {!showAdd && (
        <button className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white"
          onClick={() => setShowAdd(true)}>+ Add secret</button>
      )}
      {showAdd && (
        <form onSubmit={add} className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
          <label className="block text-sm">
            <span className="text-xs text-slate-600">Key</span>
            <input className="w-full rounded border border-slate-300 p-2 font-mono"
              list="common-secret-keys"
              placeholder="e.g. stripe.secret_key"
              value={newKey} onChange={e => setNewKey(e.target.value)} />
            <datalist id="common-secret-keys">
              {COMMON_KEYS.map(k => <option key={k} value={k} />)}
            </datalist>
          </label>
          <label className="block text-sm">
            <span className="text-xs text-slate-600">Value</span>
            <input type="password" className="w-full rounded border border-slate-300 p-2 font-mono"
              value={newVal} onChange={e => setNewVal(e.target.value)} />
          </label>
          <div className="flex gap-2">
            <button className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white" disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="text-sm px-3 py-1.5 rounded border border-slate-300"
              onClick={() => { setShowAdd(false); setNewKey(''); setNewVal(''); }}>Cancel</button>
          </div>
        </form>
      )}
    </Card>
  );
}

function Card({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h2 className="font-semibold mb-3">{title}</h2>
      {children}
    </div>
  );
}
function Count({ label, v }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold tabular-nums">{v ?? '—'}</div>
    </div>
  );
}

function EditTenantForm({ tenant, onSaved }) {
  const [f, setF] = useState({
    name: tenant.name, plan: tenant.plan, status: tenant.status,
    merchant_scope: tenant.merchant_scope || 'facility',
    default_processor: tenant.default_processor || 'stripe',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  async function save(e) {
    e.preventDefault();
    setBusy(true); setErr(null); setMsg(null);
    try { await platformApi.patchTenant(tenant.id, f); setMsg('Saved.'); onSaved(); }
    catch (e) { setErr(e.body?.message || e.message); }
    finally { setBusy(false); }
  }
  return (
    <Card title="Settings">
      <form onSubmit={save} className="grid sm:grid-cols-2 gap-3 text-sm">
        <label className="block">
          <span className="text-xs text-slate-600">Name</span>
          <input className="w-full rounded border border-slate-300 p-2"
            value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-xs text-slate-600">Plan</span>
          <input className="w-full rounded border border-slate-300 p-2"
            value={f.plan} onChange={e => setF({ ...f, plan: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-xs text-slate-600">Status</span>
          <select className="w-full rounded border border-slate-300 p-2"
            value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
            <option value="trial">trial</option>
            <option value="active">active</option>
            <option value="suspended">suspended</option>
            <option value="terminated">terminated</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-slate-600">Merchant scope</span>
          <select className="w-full rounded border border-slate-300 p-2"
            value={f.merchant_scope} onChange={e => setF({ ...f, merchant_scope: e.target.value })}>
            <option value="facility">facility</option>
            <option value="tenant">tenant</option>
          </select>
        </label>
        <div className="sm:col-span-2 flex items-center gap-2 pt-2">
          <button className="px-4 py-2 rounded bg-slate-900 text-white font-medium" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
          {msg && <span className="text-sm text-emerald-700">{msg}</span>}
          {err && <span className="text-sm text-red-700">{err}</span>}
        </div>
      </form>
    </Card>
  );
}

function SuspendToggle({ tenant, reload }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    const next = tenant.status === 'suspended' ? 'active' : 'suspended';
    try { await platformApi.patchTenant(tenant.id, { status: next }); reload(); }
    finally { setBusy(false); }
  }
  return (
    <button onClick={go} disabled={busy}
      className="px-3 py-1.5 rounded text-sm border border-slate-300 hover:bg-slate-50">
      {tenant.status === 'suspended' ? 'Reactivate' : 'Suspend'}
    </button>
  );
}
function SoftDeleteButton({ tenant, reload }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    if (!confirm(`Soft-delete ${tenant.slug}? Data is preserved.`)) return;
    setBusy(true);
    try { await platformApi.softDeleteTenant(tenant.id); reload(); }
    finally { setBusy(false); }
  }
  if (tenant.deleted_at) return null;
  return (
    <button onClick={go} disabled={busy}
      className="px-3 py-1.5 rounded text-sm bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">
      Soft-delete
    </button>
  );
}
function HardDeleteSection({ tenant }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  async function go() {
    if (confirmText !== tenant.slug) return;
    if (!confirm(`PERMANENTLY DELETE ${tenant.slug} and ALL its data? This cascades through facilities, users, patients, payments, shipments, everything. Cannot be undone.`)) return;
    setBusy(true);
    try { await platformApi.hardDeleteTenant(tenant.id); window.location.href = '/platform'; }
    finally { setBusy(false); }
  }
  return (
    <div className="bg-red-50 border border-red-300 rounded-lg p-4 space-y-2">
      <h2 className="font-semibold text-red-800">Danger zone — hard-delete</h2>
      <p className="text-sm text-red-700">
        Cascades through every tenant-scoped table. There is no undo. Type the tenant slug
        <span className="font-mono mx-1">{tenant.slug}</span> below to confirm.
      </p>
      <div className="flex gap-2">
        <input className="rounded border border-red-300 p-2 font-mono text-sm flex-1"
          value={confirmText} onChange={e => setConfirmText(e.target.value)} />
        <button onClick={go}
          disabled={busy || confirmText !== tenant.slug}
          className="px-4 py-2 rounded bg-red-700 text-white font-medium disabled:opacity-40">
          {busy ? 'Tearing down…' : 'Hard-delete tenant'}
        </button>
      </div>
    </div>
  );
}

function ResetPasswordButton({ tenantId, user }) {
  const [busy, setBusy] = useState(false);
  const [shown, setShown] = useState(null);  // { temp_password } once we have one
  async function go() {
    const custom = prompt(
      `Reset password for ${user.email}?\n\nLeave blank to auto-generate, or type a custom temp password (>= 8 chars).`);
    if (custom === null) return;
    setBusy(true);
    try {
      const body = custom.trim() ? { new_password: custom.trim() } : {};
      const r = await platformApi.resetUserPassword(tenantId, user.id, body);
      setShown({ password: r.temp_password, email: user.email });
    } catch (e) { alert(e.body?.message || e.message); }
    finally { setBusy(false); }
  }
  return (
    <>
      <button onClick={go} disabled={busy}
        className="text-xs px-2 py-1 rounded border border-amber-300 text-amber-800 hover:bg-amber-50">
        {busy ? '…' : 'Reset password'}
      </button>
      {shown && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setShown(null)}>
          <div className="bg-white rounded-lg max-w-md w-full p-5 space-y-3 shadow-xl"
            onClick={e => e.stopPropagation()}>
            <h2 className="font-semibold">Temp password for {shown.email}</h2>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 font-mono text-lg text-amber-900 break-all select-all">
              {shown.password}
            </div>
            <p className="text-xs text-slate-600">
              This is the only time this value will be shown. Deliver it to the user securely.
              They'll be forced through the change-password flow on next login.
            </p>
            <div className="flex justify-end gap-2">
              <button className="text-sm px-3 py-1.5 rounded border border-slate-300"
                onClick={() => navigator.clipboard?.writeText(shown.password)}>Copy</button>
              <button className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white"
                onClick={() => setShown(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ImpersonateButton({ tenantId, user }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    const reason = prompt(`Reason for impersonating ${user.email}? (audited)`);
    if (!reason || reason.length < 6) return;
    setBusy(true);
    try {
      const r = await platformApi.impersonate(tenantId, { user_id: user.id, reason });
      // Stash the tenant token + user, then jump into the tenant app.
      sessionStorage.setItem('m2b_token', r.token);
      sessionStorage.setItem('m2b_user', JSON.stringify(r.target_user));
      sessionStorage.setItem('m2b_impersonation_active', '1');
      window.location.href = '/';
    } catch (e) { alert(e.body?.message || e.message); }
    finally { setBusy(false); }
  }
  return (
    <button onClick={go} disabled={busy}
      className="text-xs px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
      {busy ? '…' : 'Impersonate'}
    </button>
  );
}
